import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { resolveFontScale, resolveFontStack, scaleFontSize } from "../fonts.js";
import { DIGITAL_LABEL_FONT_SIZE, DIGITAL_MESSAGE_FONT_SIZE, DIGITAL_NEXT_ITEM_FONT_SIZE, DIGITAL_SPEAKER_FONT_SIZE, DIGITAL_TITLE_FONT_SIZE, digitalClockFontSize, digitalCountdownFontSize, scaledTimerFontSize, } from "./digital-font-sizing.js";
import { zoneColor } from "./zone-colors.js";
const TOKEN_PATTERN = /^\{([a-zA-Z][\w.]*)\}$/;
function resolveToken(value, tokens) {
    if (typeof value !== "string")
        return undefined;
    const match = TOKEN_PATTERN.exec(value);
    return match?.[1] ? tokens[match[1]] : value;
}
/**
 * A layer's own `fontFamily` wins; one that sets none follows the theme's
 * `font.family` token, so switching that single token restyles every text
 * layer. `scale` is that face's own size correction times the theme's
 * `font.size` token — apply it to every font size this layer draws.
 */
function resolveLayerFont(style, tokens) {
    const name = resolveToken(style.fontFamily, tokens) ?? tokens["font.family"];
    return { family: resolveFontStack(name) ?? "inherit", scale: resolveFontScale(name, tokens) };
}
/** `origin` reads "vertical-horizontal" (e.g. `"bottom-center"`); `"center"`/`"free"` are the two single-word exceptions. Only the horizontal half matters for the flex column's cross-axis alignment here. */
function horizontalAlign(origin) {
    if (origin === "center")
        return "center";
    if (origin === "free")
        return "flex-start";
    const horizontal = origin.split("-")[1];
    if (horizontal === "right")
        return "flex-end";
    if (horizontal === "center")
        return "center";
    return "flex-start";
}
const ORIGIN_TRANSFORM = {
    "top-left": "translate(0, 0)",
    "top-center": "translate(-50%, 0)",
    "top-right": "translate(-100%, 0)",
    "center-left": "translate(0, -50%)",
    center: "translate(-50%, -50%)",
    "center-right": "translate(-100%, -50%)",
    "bottom-left": "translate(0, -100%)",
    "bottom-center": "translate(-50%, -100%)",
    "bottom-right": "translate(-100%, -100%)",
    free: "translate(0, 0)",
};
function isHourFormat12(mode) {
    return mode !== "HH:MM:SS";
}
/** `"19 AUGUST 2026 - WED"` — mirrors `ExtendedDisplayView`'s own identical `formatDateLine`, for the `clockOnly` view's date line. */
function formatDateLine(date) {
    const day = date.toLocaleDateString(undefined, { day: "2-digit" });
    const month = date.toLocaleDateString(undefined, { month: "long" });
    const year = date.toLocaleDateString(undefined, { year: "numeric" });
    const weekday = date.toLocaleDateString(undefined, { weekday: "short" });
    return `${day} ${month} ${year} - ${weekday}`.toUpperCase();
}
function formatClock(now, mode) {
    if (!isHourFormat12(mode)) {
        return now.toLocaleTimeString(undefined, {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
    }
    // `"hh:mm A"` (this app's own speaker-view default, see
    // `builtin-theme-catalog.ts`) and any other/absent mode both fall back
    // to a plain 12-hour "9:43 AM" — a full custom-format-string parser is
    // out of scope for this first pass at the renderer.
    return now.toLocaleTimeString(undefined, {
        hour12: true,
        hour: "numeric",
        minute: "2-digit",
    });
}
/** `"5:00"`/`"1:05:00"` — matches this file's own `timer`-layer countdown convention (minutes:seconds, hours only when the duration actually has one), not a clock time. */
function formatQueueDuration(durationMs) {
    const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (value) => String(value).padStart(2, "0");
    return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}
/** Merges the base `style` with whatever this layer's zone/paused overrides declare — `states[zone]` first, then `states.paused` on top, so a paused-while-in-warning layer gets both. */
function resolveStyle(layer, definition, zone, isPaused) {
    let style = { ...(layer.style ?? {}) };
    const zoneOverride = zone === "normal" ? null : zone;
    if (zoneOverride)
        style = { ...style, ...(definition.states[zoneOverride]?.[layer.id] ?? {}) };
    if (isPaused)
        style = { ...style, ...(definition.states.paused?.[layer.id] ?? {}) };
    return style;
}
/**
 * A theme opts a layer into urgency coloring by giving it a `color` in
 * `states.warning`/`critical`/`overtime` (in the built-in catalog, only
 * ever the `timer` layer) — when it has, the raw token/hex it declared
 * there is discarded in favor of `zoneColor`'s own contrast-checked
 * light-on-dark/dark-on-light pick, resolved against this theme's own
 * background. A layer the theme never opted in (no `color` in that
 * state) is untouched, still just its plain resolved `style.color`.
 */
function resolveLayerColor(layer, definition, zone, style, backgroundHex) {
    const zoneOverride = zone === "normal" ? null : zone;
    const opensIntoZoneColor = zoneOverride &&
        zoneOverride !== "paused" &&
        typeof definition.states[zoneOverride]?.[layer.id]?.color === "string";
    if (opensIntoZoneColor) {
        return zoneColor(zoneOverride, backgroundHex);
    }
    return resolveToken(style.color, definition.tokens) ?? "inherit";
}
function layerContent(layer, props) {
    switch (layer.type) {
        case "timer":
            return props.timerText;
        case "clock":
            return formatClock(props.clockNow, layer.format?.mode);
        case "nextItem":
            return props.nextItemTitle ?? "No next item";
        case "title":
            return props.currentItemTitle ?? "";
        case "speaker":
            return props.currentItemSpeaker ?? "";
        case "message":
            // Ephemeral by design (§7.5/§7.6, `use-message.ts`'s own doc
            // comment) — absent, this layer renders nothing at all rather than
            // an empty box or a "no message" placeholder sitting on screen.
            return props.messageText;
        // `progress`/`logo`/`text`/`image`: a documented gap, not a fabricated
        // render — none of these have live content this renderer resolves
        // yet (no asset pipeline, no ring/bar appearance geometry built).
        default:
            return null;
    }
}
/**
 * `style.appearance === "digital"` reproduces `ExtendedDisplayView`'s
 * fixed look (dark, uppercase, font-black, tabular-feeling digits) for a
 * theme-driven layer instead of that hardcoded component — the values
 * below are that component's own per-element clamp sizes, per layer
 * type. Every other appearance value (minimal/broadcast/ring/bar/flip)
 * still falls through to the plain generic rendering below unchanged —
 * their distinct visual treatments remain a documented, deferred gap,
 * same as before this function existed.
 */
function digitalValueStyle(layer, content) {
    switch (layer.type) {
        case "timer":
            return {
                fontSize: digitalCountdownFontSize(content.length),
                letterSpacing: "0.01em",
                tabularNums: true,
            };
        case "clock":
            return {
                fontSize: digitalClockFontSize(isHourFormat12(layer.format?.mode)),
                letterSpacing: "0.01em",
                tabularNums: true,
            };
        case "nextItem":
            return { fontSize: DIGITAL_NEXT_ITEM_FONT_SIZE, letterSpacing: "0.01em", tabularNums: false };
        case "title":
            return { fontSize: DIGITAL_TITLE_FONT_SIZE, letterSpacing: "0.03em", tabularNums: false };
        case "speaker":
            return { fontSize: DIGITAL_SPEAKER_FONT_SIZE, letterSpacing: "0.12em", tabularNums: false };
        case "message":
            return { fontSize: DIGITAL_MESSAGE_FONT_SIZE, letterSpacing: "0.03em", tabularNums: false };
        default:
            return { fontSize: "4vh", letterSpacing: "0.01em", tabularNums: false };
    }
}
/**
 * Draws a theme's `layers[]` as positioned DOM text. Shared by
 * `apps/desktop` (the native output window) and `apps/web-client` (the
 * browser display page) so both draw identically instead of two
 * independently-maintained copies. `style.appearance === "digital"`
 * gets `ExtendedDisplayView`-equivalent styling (see `digitalValueStyle`
 * above); every other/absent appearance value renders as plain styled
 * text, same as this component always has. No transitions, no
 * `progress`/`logo`/`text`/`image` layer content yet. `background`/
 * `states.*.background` are read by the caller (unchanged) — this
 * component only draws the layers on top of that.
 */
export function ThemeLayerRenderer({ definition, ...props }) {
    const backgroundHex = resolveToken(definition.background.value, definition.tokens) ?? "#000000";
    if (props.messageStyle === "fullscreen" && props.messageText) {
        const foregroundHex = definition.tokens["color.fg"] ?? "#ffffff";
        return (_jsx("div", { style: {
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: backgroundHex,
                padding: "7vw",
            }, children: _jsx("div", { style: {
                    textAlign: "center",
                    color: foregroundHex,
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    fontSize: "clamp(2rem, 8vw, 7.5rem)",
                    lineHeight: 1.05,
                    maxWidth: "90%",
                    wordBreak: "break-word",
                    animation: props.messageBlink ? "displayBlink 1s step-start infinite" : undefined,
                    textShadow: "0 0 28px rgba(255,255,255,0.16)",
                }, children: props.messageText }) }));
    }
    if (props.clockOnly) {
        const foregroundHex = definition.tokens["color.fg"] ?? "#ffffff";
        return (_jsxs("div", { style: {
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                background: backgroundHex,
                color: foregroundHex,
                textAlign: "center",
            }, children: [_jsx("div", { style: {
                        fontSize: "clamp(1.25rem, 3vw, 3rem)",
                        fontWeight: 700,
                        letterSpacing: "0.18em",
                        opacity: 0.75,
                    }, children: formatDateLine(props.clockNow) }), _jsx("div", { style: {
                        marginTop: "2vh",
                        fontSize: "clamp(5rem, 14vw, 16rem)",
                        fontWeight: 900,
                        letterSpacing: "0.02em",
                        lineHeight: 1,
                        fontVariantNumeric: "tabular-nums",
                    }, children: props.timerText })] }));
    }
    const hasVisibleMessageLayer = definition.layers.some((layer) => layer.type === "message" && layer.visible);
    return (_jsxs(_Fragment, { children: [definition.layers
                .filter((layer) => layer.visible)
                .map((layer) => {
                const style = resolveStyle(layer, definition, props.zone, props.isPaused);
                if (layer.type === "divider") {
                    const color = resolveToken(style.color, definition.tokens) ?? "currentColor";
                    const opacity = typeof style.opacity === "number" ? style.opacity : 0.2;
                    return (_jsx("div", { style: {
                            position: "absolute",
                            left: layer.anchor.x,
                            top: 0,
                            bottom: 0,
                            width: typeof style.thickness === "string" ? style.thickness : "1px",
                            background: color,
                            opacity,
                        } }, layer.id));
                }
                if (layer.type === "queue") {
                    const items = props.upcomingItems ?? [];
                    if (items.length === 0)
                        return null;
                    const [nextUp, ...rest] = items;
                    const color = resolveLayerColor(layer, definition, props.zone, style, backgroundHex);
                    const { family: fontFamily, scale: fontScale } = resolveLayerFont(style, definition.tokens);
                    const nextCaption = typeof style.nextCaption === "string" ? style.nextCaption : "Up Next";
                    const queueCaption = typeof style.queueCaption === "string" ? style.queueCaption : "Queue";
                    // Only the small section labels ("UP NEXT"/"QUEUE") go
                    // uppercase for "digital" appearance — unlike every other
                    // layer type, this one's actual content (item titles) is
                    // real schedule data meant to stay in its own natural case,
                    // the same way `nextItem`'s content already does today.
                    const captionStyle = {
                        fontSize: "0.55em",
                        fontWeight: 700,
                        letterSpacing: "0.15em",
                        opacity: 0.6,
                        textTransform: style.appearance === "digital" ? "uppercase" : undefined,
                    };
                    return (_jsxs("div", { style: {
                            position: "absolute",
                            left: layer.anchor.x,
                            top: layer.anchor.y,
                            transform: ORIGIN_TRANSFORM[layer.anchor.origin],
                            display: "flex",
                            flexDirection: "column",
                            alignItems: horizontalAlign(layer.anchor.origin),
                            gap: "0.6em",
                            fontFamily,
                            color,
                            fontSize: scaleFontSize(typeof style.fontSize === "string" ? style.fontSize : "2.2vh", fontScale),
                        }, children: [nextUp ? (_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: "0.3em" }, children: [_jsx("span", { style: captionStyle, children: nextCaption }), _jsx("span", { style: { fontSize: "1.3em", fontWeight: 800 }, children: nextUp.title }), _jsx("span", { style: { fontSize: "0.85em", fontWeight: 500, opacity: 0.7 }, children: formatQueueDuration(nextUp.durationMs) })] })) : null, rest.length > 0 ? (_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: "0.5em" }, children: [_jsx("span", { style: captionStyle, children: queueCaption }), rest.map((item, index) => (_jsx("span", { style: { opacity: 0.55 }, children: item.title }, index)))] })) : null] }, layer.id));
                }
                const content = layerContent(layer, props);
                if (content === null)
                    return null;
                const caption = typeof style.caption === "string" ? style.caption : null;
                const color = resolveLayerColor(layer, definition, props.zone, style, backgroundHex);
                const { family: fontFamily, scale: fontScale } = resolveLayerFont(style, definition.tokens);
                const opacity = typeof style.opacity === "number" ? style.opacity : 1;
                const isDigital = style.appearance === "digital";
                const baseFontSize = typeof style.fontSize === "string" ? style.fontSize : "4vh";
                // Applies regardless of "digital" appearance: a `timer` layer's
                // text can grow arbitrarily long (deep overtime, an
                // hours-long duration), and a theme that sets its own custom
                // `fontSize` (not the "digital" preset, which already shrinks
                // via `digitalCountdownFontSize`) needs the same protection
                // against overflowing into whatever's next to it.
                const fontSize = layer.type === "timer"
                    ? scaledTimerFontSize(baseFontSize, content.length)
                    : baseFontSize;
                const digital = digitalValueStyle(layer, content);
                // The same red "lower third" alert-banner box `ExtendedDisplayView`
                // gives an un-themed display's message — a theme's own `message`
                // layer used to just be plain positioned text like any other
                // layer (title/speaker/etc.), with none of that box or its
                // guaranteed-legible white text, so `"lower"`-style messages
                // looked completely different depending on whether a theme
                // happened to be assigned. White text is intentionally forced
                // here rather than the theme's own resolved `color` — a theme's
                // foreground token has no obligation to contrast against red.
                const isMessageLayer = layer.type === "message";
                return (_jsxs("div", { style: {
                        position: "absolute",
                        left: layer.anchor.x,
                        top: layer.anchor.y,
                        transform: ORIGIN_TRANSFORM[layer.anchor.origin],
                        display: "flex",
                        flexDirection: "column",
                        alignItems: horizontalAlign(layer.anchor.origin),
                        opacity,
                        whiteSpace: isMessageLayer ? "normal" : "nowrap",
                        maxWidth: isMessageLayer ? "60vw" : undefined,
                        wordBreak: isMessageLayer ? "break-word" : undefined,
                        fontSize: isDigital ? undefined : scaleFontSize(fontSize, fontScale),
                        background: isMessageLayer ? "#ef4444" : undefined,
                        borderRadius: isMessageLayer ? "0.2em" : undefined,
                        padding: isMessageLayer ? "0.35em 0.9em" : undefined,
                        // The box and its text blink together, as one unit — the
                        // animation lives on this same outer element (background
                        // included) rather than being split off onto a separate
                        // layer.
                        animation: isMessageLayer && props.messageBlink
                            ? "displayBlink 1s step-start infinite"
                            : undefined,
                    }, children: [caption ? (_jsx("span", { style: {
                                fontFamily,
                                fontWeight: isDigital ? 900 : 600,
                                fontSize: isDigital
                                    ? scaleFontSize(DIGITAL_LABEL_FONT_SIZE, fontScale)
                                    : "0.35em",
                                letterSpacing: "0.08em",
                                textTransform: isDigital ? "uppercase" : undefined,
                                color: isMessageLayer ? "#ffffff" : color,
                                opacity: isDigital ? 1 : 0.7,
                            }, children: caption })) : null, _jsx("span", { style: {
                                fontFamily,
                                fontWeight: isDigital
                                    ? 900
                                    : typeof style.fontWeight === "number"
                                        ? style.fontWeight
                                        : 400,
                                fontSize: isDigital ? scaleFontSize(digital.fontSize, fontScale) : "1em",
                                letterSpacing: isDigital ? digital.letterSpacing : undefined,
                                textTransform: isDigital ? "uppercase" : undefined,
                                color: isMessageLayer ? "#ffffff" : color,
                                fontVariantNumeric: isDigital && digital.tabularNums
                                    ? "tabular-nums"
                                    : !isDigital && layer.type === "timer"
                                        ? "tabular-nums"
                                        : undefined,
                            }, children: content })] }, layer.id));
            }), props.messageText && !hasVisibleMessageLayer ? (_jsx(FallbackLowerThird, { text: props.messageText, blink: props.messageBlink === true })) : null] }));
}
/**
 * A lower-third message for a theme that has no `message` layer of its own (the built-in
 * Audience layout, and any imported theme that omits one). Without this, an operator's
 * "Lower Third" message was silently dropped on those screens. It matches the look the
 * theme's own message layer gets — a red box with white text — and sits at the bottom
 * centre, so "Lower Third" means the same thing on every theme.
 */
function FallbackLowerThird({ text, blink, }) {
    return (_jsx("div", { role: "status", style: {
            position: "absolute",
            left: "50%",
            bottom: "6%",
            transform: "translateX(-50%)",
            maxWidth: "80vw",
            background: "#ef4444",
            color: "#ffffff",
            borderRadius: "0.2em",
            padding: "0.35em 0.9em",
            textAlign: "center",
            fontSize: DIGITAL_MESSAGE_FONT_SIZE,
            fontWeight: 800,
            letterSpacing: "0.03em",
            textTransform: "uppercase",
            wordBreak: "break-word",
            animation: blink ? "displayBlink 1s step-start infinite" : undefined,
        }, children: text }));
}
//# sourceMappingURL=theme-layer-renderer.js.map